// frontend/src/pages/Profile.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProfile, updateProfile } from '../services/api';
import {
  Card, Avatar, Badge, Btn, Modal, Field, Input, Textarea,
  ProgressBar, Stars, Spinner, SectionTitle, RatingPill,
  scoreToCalibration, scoreToLabel
} from '../components/UI';

const KR_COLORS = {
  complete: 'var(--navy-500)', in_progress: 'var(--amber-500)',
  at_risk: 'var(--red-500)', not_started: 'var(--gray-200)',
};
const STATUS_LABELS = {
  on_track: 'On track', at_risk: 'At risk',
  complete: 'Complete', not_started: 'Not started',
};
const STATUS_COLORS = {
  on_track: 'green', at_risk: 'amber', complete: 'blue', not_started: 'gray',
};

function StatBox({ label, value, sub, color = 'var(--text-1)' }) {
  return (
    <div style={{ textAlign: 'center', padding: '14px 10px', background: 'var(--gray-100)', borderRadius: 10 }}>
      <div style={{ fontSize: 26, fontWeight: 500, color, lineHeight: 1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11, fontWeight: 500, marginTop: 4, color: 'var(--text-2)' }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ScoreBar({ label, value, max = 5 }) {
  if (!value) return null;
  const pct = (value / max) * 100;
  const color = value >= 4 ? 'var(--navy-500)' : value >= 3 ? 'var(--amber-500)' : 'var(--red-500)';
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 500, color }}>{value}</span>
      </div>
      <ProgressBar value={pct} color={color} height={5} />
    </div>
  );
}

function ReviewTrend({ history }) {
  if (!history?.length) return null;
  const max = 5, w = 280, h = 60, pad = 12;
  const pts = history.map((r, i) => {
    const x = pad + (i / Math.max(history.length - 1, 1)) * (w - pad * 2);
    const y = h - pad - ((r.overall_score / max) * (h - pad * 2));
    return { x, y, score: r.overall_score, cycle: r.cycle_name };
  });
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return (
    <div>
      <svg width={w} height={h} style={{ overflow: 'visible' }}>
        <path d={d} fill="none" stroke="var(--navy-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="4" fill="var(--navy-500)" />
            <text x={p.x} y={h - 1} fontSize="9" fill="var(--text-3)" textAnchor="middle">{p.cycle?.replace('Q','Q')}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function Profile() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user: me } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({ name: '', department: '', job_title: '', password: '' });
  const [activeTab, setActiveTab] = useState('overview');

  const userId = parseInt(id, 10);
  const isSelf = me?.id === userId || me?.user_id === userId;

  useEffect(() => {
    let active = true;   // ignore a slow response after navigating to another profile
    setLoading(true);
    getProfile(userId)
      .then(d => {
        if (!active) return;
        setData(d);
        setForm({ name: d.user.name, department: d.user.department || '', job_title: d.user.job_title || '', password: '' });
      })
      .catch(() => { if (active) navigate('/'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId]);

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await updateProfile(userId, form);
      const d = await getProfile(userId);
      setData(d); setEditing(false);
    } catch (err) { alert(err.response?.data?.error || 'Error saving'); }
    finally { setSaving(false); }
  };

  if (loading) return <Spinner />;
  if (!data)   return null;

  const { user, reports = [], latest_review, review_history = [], okrs = [],
          feedback_summary, recent_feedback = [], career, dev_stats, checkins = [] } = data;

  const ROLE_COLOR  = { admin: 'red', dept_head: 'green', manager: 'amber', employee: 'blue' };
  const ROLE_LABELS = { admin: 'Admin', dept_head: 'Department Head', manager: 'Manager', employee: 'Employee' };
  const tabs = ['overview', 'performance', 'okrs', 'feedback', 'growth'];

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      {/* Back nav */}
      <button onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 13, marginBottom: 20, padding: 0 }}>
        ← Back
      </button>

      {/* Hero card */}
      <Card style={{ marginBottom: 20, padding: '24px 28px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
          {/* Big avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Avatar initials={user.avatar_initials} size={72} />
            <div style={{
              position: 'absolute', bottom: 2, right: 2,
              width: 14, height: 14, borderRadius: '50%',
              background: 'var(--navy-500)', border: '2px solid var(--surface)',
            }} />
          </div>

          {/* Identity */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <h2 style={{ fontSize: 22, fontWeight: 400 }}>{user.name}</h2>
              <Badge color={ROLE_COLOR[user.role]}>{ROLE_LABELS[user.role] || user.role}</Badge>
              {latest_review?.calibration && <RatingPill value={latest_review.calibration} />}
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 3 }}>{user.job_title || '—'}</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {user.department && <span>🏢 {user.department}</span>}
              {user.email      && <span>✉ {user.email}</span>}
              {user.manager_name && (
                <span>
                  Reports to{' '}
                  <Link to={`/profile/${user.manager_id}`} style={{ color: 'var(--navy-600)', textDecoration: 'underline' }}>
                    {user.manager_name}
                  </Link>
                </span>
              )}
              <span>Joined {new Date(user.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}</span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {(isSelf || me?.role === 'admin') && (
              <Btn variant="secondary" size="sm" onClick={() => setEditing(true)}>Edit profile</Btn>
            )}
            {!isSelf && (
              <Link to="/checkins">
                <Btn size="sm">Schedule 1:1</Btn>
              </Link>
            )}
          </div>
        </div>

        {/* Quick stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10, marginTop: 24 }}>
          <StatBox
            label="Overall rating"
            value={latest_review?.overall_score ?? '—'}
            sub={latest_review?.cycle_name}
            color="var(--navy-600)"
          />
          <StatBox
            label="OKR progress"
            value={okrs.length ? `${Math.round(okrs.reduce((s, o) => s + o.progress, 0) / okrs.length)}%` : '—'}
            sub={`${okrs.length} active`}
          />
          <StatBox
            label="Feedback"
            value={feedback_summary?.total || 0}
            sub={feedback_summary?.avg_stars ? `${feedback_summary.avg_stars}★ avg` : 'responses'}
          />
          <StatBox
            label="Dev plan"
            value={dev_stats?.total ? `${dev_stats.done}/${dev_stats.total}` : '—'}
            sub="milestones done"
          />
          {career && (
            <StatBox
              label="Career level"
              value={career.progress_pct + '%'}
              sub={`to ${career.target_level || 'next level'}`}
              color="var(--blue-700)"
            />
          )}
        </div>

        {/* Direct reports */}
        {reports?.length > 0 && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-3)', marginBottom: 10 }}>
              Direct reports · {reports.length}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {reports.map(r => (
                <Link key={r.id} to={`/profile/${r.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', background: 'var(--gray-100)', borderRadius: 20, textDecoration: 'none' }}>
                  <Avatar initials={r.avatar_initials} size={22} />
                  <span style={{ fontSize: 12, color: 'var(--text-1)' }}>{r.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '0.5px solid var(--border)', paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            style={{
              padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: activeTab === t ? 500 : 400,
              color: activeTab === t ? 'var(--navy-600)' : 'var(--text-3)',
              borderBottom: `2px solid ${activeTab === t ? 'var(--navy-600)' : 'transparent'}`,
              marginBottom: -1, textTransform: 'capitalize',
            }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW tab ── */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Latest review */}
          <Card>
            <SectionTitle>Latest review</SectionTitle>
            {latest_review ? (
              <>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12 }}>{latest_review.cycle_name}</div>
                <ScoreBar label="Delivery"      value={latest_review.delivery_score} />
                <ScoreBar label="Collaboration" value={latest_review.collab_score} />
                <ScoreBar label="Impact"        value={latest_review.impact_score} />
                {latest_review.summary_notes && (
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 12, lineHeight: 1.6, borderLeft: '2px solid var(--navy-100)', paddingLeft: 10, fontStyle: 'italic' }}>
                    "{latest_review.summary_notes}"
                  </div>
                )}
              </>
            ) : <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No reviews yet.</p>}
          </Card>

          {/* Career snapshot */}
          <Card>
            <SectionTitle>Career progress</SectionTitle>
            {career ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>
                  <span>{career.current_level}</span>
                  <span>{career.target_level}</span>
                </div>
                <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                  <div style={{ height: '100%', width: `${career.progress_pct}%`, background: 'var(--navy-500)', borderRadius: 10 }} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>
                  <span style={{ fontWeight: 500, color: 'var(--navy-600)' }}>{career.progress_pct}%</span> towards {career.target_level}
                </div>
                {dev_stats?.total > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    Dev plan: <strong>{dev_stats.done}</strong> of <strong>{dev_stats.total}</strong> milestones complete
                    {dev_stats.in_progress > 0 && ` · ${dev_stats.in_progress} in progress`}
                  </div>
                )}
              </>
            ) : <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No career data yet.</p>}
          </Card>

          {/* Recent 1:1s */}
          <Card>
            <SectionTitle>Recent 1:1s</SectionTitle>
            {checkins.length ? checkins.map((c, i) => (
              <div key={i} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: i < checkins.length - 1 ? '0.5px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>
                    {new Date(c.scheduled_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <Badge color={c.status === 'complete' ? 'green' : c.status === 'overdue' ? 'red' : 'blue'}>{c.status}</Badge>
                </div>
                {c.action_items && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Actions: {c.action_items}</div>}
              </div>
            )) : <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No check-ins recorded.</p>}
          </Card>

          {/* Feedback snapshot */}
          <Card>
            <SectionTitle>Feedback snapshot</SectionTitle>
            {feedback_summary?.total > 0 ? (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <div style={{ flex: 1, background: 'var(--navy-50)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--navy-600)' }}>{feedback_summary.avg_stars}</div>
                    <div style={{ fontSize: 10, color: 'var(--navy-600)' }}>Overall ★</div>
                  </div>
                  <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 500 }}>{feedback_summary.total}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-3)' }}>Responses</div>
                  </div>
                </div>
                <ScoreBar label="Technical"     value={feedback_summary.avg_technical} />
                <ScoreBar label="Collaboration" value={feedback_summary.avg_collab} />
                <ScoreBar label="Visibility"    value={feedback_summary.avg_visibility} />
              </>
            ) : <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No feedback received yet.</p>}
          </Card>
        </div>
      )}

      {/* ── PERFORMANCE tab ── */}
      {activeTab === 'performance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Rating trend */}
          {review_history?.length > 1 && (
            <Card>
              <SectionTitle>Rating trend</SectionTitle>
              <ReviewTrend history={review_history} />
            </Card>
          )}

          {/* All reviews */}
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '0.5px solid var(--border)' }}>
              <SectionTitle>All review cycles</SectionTitle>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)' }}>
                  {['Cycle','Delivery','Collaboration','Impact','Overall','Calibration'].map(h => (
                    <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', fontWeight: 500, borderBottom: '0.5px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {review_history?.length ? review_history.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>{r.cycle_name}</td>
                    <td style={{ padding: '10px 16px', color: r.delivery_score >= 4 ? 'var(--navy-600)' : 'var(--text-2)' }}>{r.delivery_score ?? '—'}</td>
                    <td style={{ padding: '10px 16px', color: r.collab_score >= 4 ? 'var(--navy-600)' : 'var(--text-2)' }}>{r.collab_score ?? '—'}</td>
                    <td style={{ padding: '10px 16px', color: r.impact_score >= 4 ? 'var(--navy-600)' : 'var(--text-2)' }}>{r.impact_score ?? '—'}</td>
                    <td style={{ padding: '10px 16px', fontWeight: 500, color: 'var(--navy-600)' }}>{r.overall_score}</td>
                    <td style={{ padding: '10px 16px' }}><RatingPill value={r.calibration} /></td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} style={{ padding: '24px 16px', color: 'var(--text-3)', textAlign: 'center' }}>No review history.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ── OKRs tab ── */}
      {activeTab === 'okrs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {okrs.length ? okrs.map(obj => (
            <Card key={obj.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 3 }}>{obj.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    Due {obj.due_date ? new Date(obj.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 20, fontWeight: 500, color: obj.status === 'at_risk' ? 'var(--amber-500)' : 'var(--navy-500)' }}>{obj.progress}%</span>
                  <Badge color={STATUS_COLORS[obj.status]}>{STATUS_LABELS[obj.status]}</Badge>
                </div>
              </div>
              <ProgressBar value={obj.progress} color={obj.status === 'at_risk' ? 'var(--amber-500)' : 'var(--navy-500)'} height={6} />
              {obj.key_results?.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {obj.key_results.map((kr, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: KR_COLORS[kr.status], flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{kr.title}</span>
                      <Badge color={kr.status === 'complete' ? 'green' : kr.status === 'at_risk' ? 'red' : 'gray'} >{kr.status.replace('_',' ')}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )) : (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)' }}>No OKRs found.</div>
          )}
        </div>
      )}

      {/* ── FEEDBACK tab ── */}
      {activeTab === 'feedback' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Scores */}
          {feedback_summary?.total > 0 && (
            <Card>
              <SectionTitle>360 score summary · {feedback_summary.total} responses</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
                <StatBox label="Overall" value={feedback_summary.avg_stars} sub="out of 5 ★" color="var(--amber-700)" />
                <StatBox label="Technical" value={feedback_summary.avg_technical} color="var(--navy-600)" />
                <StatBox label="Collaboration" value={feedback_summary.avg_collab} color="var(--blue-700)" />
                <StatBox label="Visibility" value={feedback_summary.avg_visibility} color="var(--text-2)" />
              </div>
              <ScoreBar label="Technical skill"  value={feedback_summary.avg_technical} />
              <ScoreBar label="Collaboration"     value={feedback_summary.avg_collab} />
              <ScoreBar label="Stakeholder visibility" value={feedback_summary.avg_visibility} />
            </Card>
          )}

          {/* Quotes */}
          {recent_feedback?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SectionTitle>Recent feedback</SectionTitle>
              {recent_feedback.map((f, i) => (
                <Card key={i}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <Avatar initials={f.from_initials} size={30} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{f.from_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        {new Date(f.submitted_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                    <div style={{ marginLeft: 'auto' }}><Stars value={f.overall_stars} /></div>
                  </div>
                  {f.quote && (
                    <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, borderLeft: '2px solid var(--navy-100)', paddingLeft: 12, fontStyle: 'italic' }}>
                      "{f.quote}"
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {!feedback_summary?.total && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)' }}>No feedback received yet.</div>
          )}
        </div>
      )}

      {/* ── GROWTH tab ── */}
      {activeTab === 'growth' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {career ? (
            <Card>
              <SectionTitle>{career.track} track · career progression</SectionTitle>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{career.current_level}</span>
                  <span style={{ color: 'var(--text-3)', fontSize: 12 }}> → {career.target_level}</span>
                </div>
                <span style={{ fontSize: 22, fontWeight: 500, color: 'var(--navy-600)' }}>{career.progress_pct}%</span>
              </div>
              <div style={{ height: 10, background: 'var(--gray-100)', borderRadius: 10, overflow: 'hidden', marginBottom: 6 }}>
                <div style={{ height: '100%', width: `${career.progress_pct}%`, background: 'var(--navy-500)', borderRadius: 10, transition: 'width 0.4s' }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{career.progress_pct}% towards next level</div>
            </Card>
          ) : (
            <Card><p style={{ fontSize: 13, color: 'var(--text-3)' }}>No career ladder data set up yet.</p></Card>
          )}

          {/* Dev plan */}
          {dev_stats?.total > 0 && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <SectionTitle>Development plan</SectionTitle>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{dev_stats.done}/{dev_stats.total} complete</span>
              </div>
              <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                <div style={{ height: '100%', width: `${Math.round((dev_stats.done / dev_stats.total) * 100)}%`, background: 'var(--navy-500)', borderRadius: 10 }} />
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
                {dev_stats.done} milestone{dev_stats.done !== 1 ? 's' : ''} complete
                {dev_stats.in_progress > 0 && `, ${dev_stats.in_progress} in progress`}
                {(dev_stats.total - dev_stats.done - dev_stats.in_progress) > 0 &&
                  `, ${dev_stats.total - dev_stats.done - dev_stats.in_progress} upcoming`}.
              </p>
              <div style={{ marginTop: 12 }}>
                <Link to="/career" style={{ fontSize: 13, color: 'var(--navy-600)', textDecoration: 'underline' }}>
                  View full development plan →
                </Link>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Edit profile modal */}
      <Modal open={editing} onClose={() => setEditing(false)} title="Edit profile" width={420}>
        <form onSubmit={handleSave}>
          <Field label="Full name" required>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Department">
              <Input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} placeholder="Engineering" />
            </Field>
            <Field label="Job title">
              <Input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} placeholder="Senior Engineer" />
            </Field>
          </div>
          <Field label="New password (leave blank to keep)">
            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => setEditing(false)}>Cancel</Btn>
            <Btn disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
